import { useEffect, useState } from 'react'

import { useSession } from 'next-auth/react'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Modal from '@components/Modal'
import { PrismicRichText } from '@prismicio/react'

import type { CountdownRenderProps } from 'react-countdown'
import Countdown from 'react-countdown'

import membersMarketData from '@data/memberMarket.json'

import type { GetCollectionProductsPerPageReturn } from '@lib/operations/collection'
import { getCollectionProductsPerPage } from '@lib/operations/collection'
import useSubscriptions from '@lib/subscription-box/use-subscriptions'
import { isMembersMarketOpen, resolveDateTokens } from '@lib/utils'

import MobileLogo from '@components/global/MobileLogo'
import Spinner from '@components/Spinner'
import ProductsListContainer from '@components/products/ProductsListContainer'

export default function MembersMarket() {
	const router = useRouter()
	const { loading: subscriptionsLoading, subscriptions, subscriptionLoading } = useSubscriptions()
	const [collectionHandle, setCollectionHandle] = useState<string>()
	const [collectionData, setCollectionData] = useState<GetCollectionProductsPerPageReturn>()
	const { data: session } = useSession()
	const [loading, setLoading] = useState(true)
	const [quickShopProductIdSelected, setQuickShopProductIdSelected] = useState<string | null>(null)

	const accessToken = (session as any)?.shopifyAccessToken
	const isLoggedIn = session !== null && accessToken
	const membersMarketAccessRaw = (membersMarketData as any).members_market_access
	// Handle empty strings, null, undefined - default to 'Active Subscription Only'
	const membersMarketAccess = (membersMarketAccessRaw && membersMarketAccessRaw.trim()) || 'Active Subscription Only'
	
	// Get banner background color from Prismic, fallback to Tailwind class if not provided
	const marketBannerBgColor = (membersMarketData as any).market_banner_bg_color

	const modalButtonUrl = (() => {
		const linkField = (membersMarketData as any).modal_button_link
		const baseUrl: string | undefined =
			typeof linkField === 'string' ? linkField : linkField?.url
		if (!baseUrl) return undefined
		const email = session?.user?.email
		if (!email) return baseUrl
		return `${baseUrl}#email=${encodeURIComponent(email)}`
	})()

	const collectionsFilter = membersMarketData.filter_collections
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const defaultCollection = collectionsFilter.find((cf) => cf.key === '*')!

	const additionalFilterOptions = collectionsFilter.map((cf) => ({
		title: cf.name,
		path: `/members-market${cf.key && cf.key !== '*' ? `?collection_name=${cf.key}` : ''}`,
		active: cf.collection_slug === collectionHandle
	}))

	// Check for force market open URL parameter
	const forceMarketOpenParam = router.query.forcemarketopen as string | undefined

	const { isCurrentlyOpen: isMambersMarketCurrentlyOpen, isBeforeOpenDate, isBeforeCloseDate, computedStartDate, computedEndDate } =
		isMembersMarketOpen(membersMarketData, forceMarketOpenParam)

	const startDate = new Date(computedStartDate)
	const startTokens = {
		startDay: String(startDate.getUTCDate()),
		startMonthNumber: String(startDate.getUTCMonth() + 1)
	}

	// Find the most recent subscription with a customized box
	const lastSubscriptionBox = subscriptions?.[0]?.lastBox
	const hasCustomized =
		lastSubscriptionBox?.tags?.includes('rmc_box_customized') &&
		new Date(lastSubscriptionBox.processedAt) >= new Date(Date.now() - 20 * 24 * 60 * 60 * 1000) // Checks to see if the user has a box that has been customized in the last 20 days

	// Check if there's an active subscription or one with queued charges
	const hasSubscriptionOrder = subscriptions?.some(
		(sub) => sub.status === 'active' || sub.has_queued_charges
	)

	/* ***************** */
	/* MODAL LOGIC START */
	/* ***************** */
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [copyStatus, setCopyStatus] = useState('COPY')

	// Checks access based on members_market_access setting
	const saleMemberType = () => {
		const accessValue = membersMarketAccess?.trim()
		if (accessValue === 'All Can Access') {
			return true // Everyone can access
		}
		if (accessValue === 'Logged in Only') {
			return isLoggedIn // Only logged-in users
		}
		// Default: 'Active Subscription Only'
		return hasSubscriptionOrder // Only users with active subscriptions
	}

	useEffect(() => {
		// Checks to see if there is a cookie for the members modal and if so, it does not show on load
		const setShowCookie = getCookie('membersModal')
		if (!setShowCookie) {
			setIsModalOpen(true)
		}
	}, [])

	// Modal copy to clipboard
	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text)
			setCopyStatus('COPIED')
			setTimeout(() => setCopyStatus('COPY'), 2000) // Reset status after 2 seconds
		} catch (error) {
			console.error('Failed to copy text:', error)
		}
	}

	const modalMemberType = () => {
		if (!membersMarketData?.modal_active) {
			return false
		}
		if (membersMarketData.modal_user_type === 'Active Subscription Logged In and Customized') {
			return hasCustomized
		} else if (membersMarketData.modal_user_type === 'Active Subscription Logged In') {
			return hasSubscriptionOrder
		}
		return isLoggedIn
	}

	// Sets the cookie for the modal
	const setModalCookie = (duration: string) => {
		const date = new Date()

		// Define the time multipliers
		const timeMultiplier: { m: number; h: number } = {
			m: 60 * 1000, // minutes to milliseconds
			h: 60 * 60 * 1000 // hours to milliseconds
		}

		// Parse the duration string to extract the amount and unit
		const match = duration.match(/^(\d+)(m|h)$/)
		if (match) {
			const [_, amount, unit] = match

			const milliseconds = parseInt(amount, 10) * timeMultiplier[unit as 'm' | 'h']

			date.setTime(date.getTime() + milliseconds)
			document.cookie = `membersModal=hide; expires=${date.toUTCString()}; path=/`
		} else {
			console.error("Invalid cookie time format. Use '30m', '1h', etc.")
		}
	}

	// Gets the cookie for the modal to open or not open
	// Gets the cookie for the modal to open or not open
	const getCookie = (name: string): string | null => {
		const cookies = document.cookie.split(';')
		for (let i = 0; i < cookies.length; i++) {
			const cookie = cookies[i].trim()
			if (cookie.startsWith(`${name}=`)) {
				// Use template literals here
				return cookie.substring(name.length + 1)
			}
		}
		return null
	}

	const closeModal = () => {
		setIsModalOpen(false)
		setModalCookie(membersMarketData?.modal_cookie_duration || '30m')
	}
	/* *************** */
	/* MODAL LOGIC END */
	/* *************** */

	const fetchCollectionData = async (collectionPath: string): Promise<void> => {
		try {
			// setLoading(true)
			const collectionDataResponse = await getCollectionProductsPerPage(collectionPath)

			if (!collectionDataResponse || !collectionDataResponse.collection) {
				// TODO: redirecto to 404?
				return
			}
			const collectionDataParsed = {
				...collectionDataResponse,
				filters: collectionDataResponse.filters.filter((f) => f.type === 'PRICE_RANGE')
			}
			setCollectionData(collectionDataParsed)
		} catch (error) {
			// TODO: SHOW 404 PRODUCT
		} finally {
			setLoading(false)
		}
	}

	// LOAD PAGE
	useEffect(() => {
		// USER is not logged in
		// if (!isLoggedIn) setLoading(false)
		setLoading(session === undefined || subscriptionLoading)
	}, [session, subscriptionLoading])

	useEffect(() => {
		if (saleMemberType() && isMambersMarketCurrentlyOpen) {
			if (!collectionData) {
				// const p = await getProductBySlug('')
				const urlParams = new URLSearchParams(window.location.search)
				const collectionName = urlParams.get('collection_name') || ''
				const collectionObj =
					collectionsFilter.find((c) => c.key === collectionName.toLowerCase()) || defaultCollection

				setCollectionHandle(collectionObj.collection_slug)

				fetchCollectionData(collectionObj.collection_slug)
			}
		}
		// NOTE: Change back to
	}, [saleMemberType])

	const renderer = (
		{ hours, minutes, seconds, days }: CountdownRenderProps,
		isCloseTime = true
	) => {
		const twoDigitsNumber = (n: number) => String(n).padStart(2, '0')
		return (
			<div className="relative h-fit w-fit flex border-bbx-pearl border-2 text-bbx-pearl">
				<div className="flex flex-row py-3 px-3 divide-x divide-white font-bold">
					<div className="flex flex-col px-2 text-center">
						<span className="text-2xl">{twoDigitsNumber(days)}</span>
						<span className="font-normal text-xs">DAYS</span>
					</div>
					<div className="flex flex-col px-2 text-center">
						<span className="text-2xl">{twoDigitsNumber(hours)}</span>
						<span className="font-normal text-xs">HOURS</span>
					</div>
					<div className="flex flex-col px-2 text-center">
						<span className="text-2xl">{twoDigitsNumber(minutes)}</span>
						<span className="font-normal text-xs">MINUTES</span>
					</div>
					<div className="flex flex-col px-2 text-center">
						<span className="text-2xl">{twoDigitsNumber(seconds)}</span>
						<span className="font-normal text-xs">SECONDS</span>
					</div>
				</div>
				<h2 className="absolute flex top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
					<span 
						className={`px-2 whitespace-nowrap ${!marketBannerBgColor ? 'bg-bbx-glow' : ''}`}
						style={marketBannerBgColor ? { backgroundColor: marketBannerBgColor } : undefined}
					>
						{isCloseTime ? 'Market closes in...' : 'Market opens in...'}
					</span>
				</h2>
			</div>
		)
	}

	if (loading || subscriptionsLoading)
		return (
			<>
				<NextSeo title="Members Market" />
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 pointer-events-none">
					<Spinner />
				</div>
			</>
		)

	return (
		<>
			<NextSeo title="Members Market" />
			{/* eslint-disable-next-line no-nested-ternary */}
			{saleMemberType() ? (
				isMambersMarketCurrentlyOpen ? (
					<>
						{/* Hero */}
						<header 
							className={`relative py-12 mb-16 ${!marketBannerBgColor ? 'bg-bbx-glow' : ''}`}
							style={marketBannerBgColor ? { backgroundColor: marketBannerBgColor } : undefined}
						>
							<div className="container flex flex-col md:flex-row place-content-center gap-8 md:gap-16">
								{/* Category title - A11Y: text shadow improves contrast on colored backgrounds */}
								<div className="flex flex-row items-start gap-3">
									<MobileLogo className="h-[58px] w-auto fill-current text-bbx-pearl flex-shrink-0 pt-[2px]" />
									<div className="flex flex-col text-left text-bbx-pearl" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
										<h1 className="title text-3xl md:text-4xl pb-2">
											{resolveDateTokens(membersMarketData.market_open_subscriber_title[0].text)}
										</h1>
										<span className="text-base font-medium">
											{resolveDateTokens(membersMarketData.market_open_subscriber_subtitle[0].text)}
										</span>
									</div>
								</div>
								{/* TODO: receive date to start countdown */}
								<div className="flex justify-center">
									<Countdown date={computedEndDate} renderer={renderer} />
								</div>
							</div>
						</header>

						{collectionData && (
							<ProductsListContainer
								filterMenuOptions={additionalFilterOptions}
								collectionSlug={collectionHandle as string}
								products={collectionData.products}
								pageInfo={collectionData.pageInfo}
								filtersOptions={collectionData.filters}
								isMemberMarket
								quickShopProductIdSelected={quickShopProductIdSelected}
								setQuickShopProductIdSelected={setQuickShopProductIdSelected}
							/>
						)}

						{modalMemberType() && (
							<>
								<Modal isOpen={isModalOpen} closeModal={closeModal} desktopStyle="floating">
									<div className="text-center p-20 max-sm:p-10">
										{membersMarketData.modal_title && (
											<h1 className="title text-3xl md:text-4xl mb-4">
												<PrismicRichText field={membersMarketData.modal_title as any} />
											</h1>
										)}
										{membersMarketData.modal_description && (
											<div className="text-xl max-w-[500px] mb-10">
												<PrismicRichText field={membersMarketData.modal_description as any} />
											</div>
										)}
										{membersMarketData.modal_promo_code && (
											<>
												<div className="copy-button mt-[12px] h-[45px] border-2 border-dashed flex text-center justify-between flex-auto max-w-[350px] m-auto">
													<input
														id="copyvalue"
														type="text"
														readOnly
														value={membersMarketData.modal_promo_code}
														aria-label="Promo code to copy"
														className="w-full h-full border-0 outline-none text-center text-[18px] font-bold"
													/>
													<button
														type="button"
														onClick={() =>
															copyToClipboard(membersMarketData.modal_promo_code || '')
														}
														className="copybtn pt-[2px] px-[30px] bg-bbx-spark text-white"
													>
														{copyStatus}
													</button>
												</div>
												<div className="mt-4 italic">Use Promo at Checkout</div>
											</>
										)}
										{modalButtonUrl && (membersMarketData as any).modal_button_title && (
											<div className="mt-6 flex justify-center">
												<a
													href={modalButtonUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="inline-block bg-bbx-spark text-white font-bold px-8 py-3 hover:bg-bbx-style"
												>
													{(membersMarketData as any).modal_button_title}
												</a>
											</div>
										)}
									</div>
								</Modal>
								{membersMarketData.modal_quick_open_link_text && (
									<button
										type="button"
										className="promo-bubble fixed left-3 bottom-3 bg-bbx-spark text-white px-4 py-2 font-bold hover:bg-bbx-style z-10 lg:px-6 lg:py-4 lg:text-lg"
										onClick={() => setIsModalOpen(true)}
									>
										{membersMarketData.modal_quick_open_link_text}
									</button>
								)}
							</>
						)}
					</>
				) : (
					<div className="members-market-bg h-full min-h-[80vh]">
						<header className="flex py-12 mb-16 bg-bbx-glow h-full items-center opacity-95">
							<div className="flex flex-col container items-center gap-16">
								{/* Category title - A11Y: text shadow improves contrast on colored backgrounds */}
								<div className="flex flex-col text-center text-bbx-pearl max-w-[500px]" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
									<MobileLogo className="w-10 fill-current mx-auto mb-3" />
									<h1 className="title text-3xl md:text-4xl pb-2">
										{resolveDateTokens(membersMarketData.market_close_subscriber_title[0].text, startTokens)}
									</h1>
									<p className="text-xl font-medium">
										<span>{resolveDateTokens(membersMarketData.market_close_subscriber_subtitle[0].text, startTokens)}</span>{' '}
										<Link legacyBehavior href={membersMarketData.market_close_learn_more_path_link}>
											<a className="text-bbx-pearl underline" target="_blank">
												Learn more
											</a>
										</Link>
									</p>
								</div>
								{/* TODO: receive date to start countdown */}
								{isBeforeCloseDate && (
									<div className="flex justify-center">
										<Countdown
											date={computedStartDate}
											renderer={(props) => renderer(props, false)}
										/>
									</div>
								)}
							</div>
						</header>
					</div>
				)
			) : (
				<div className="members-market-bg grid grid-rows-2 md:grid-cols-2 md:grid-rows-1 h-full w-full min-h-[80vh]">
					<div className="opacity-95 flex bg-bbx-glow justify-center md:justify-end py-40 md:pr-[60px]">
						<div className="flex flex-col place-content-center gap-8">
							{/* A11Y: text shadow improves contrast on colored backgrounds */}
							<div className="flex flex-col text-bbx-pearl items-center md:items-start" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
								<h1 className="title text-3xl md:text-4xl pb-2">
									{membersMarketData.non_subscriber_title[0].text}
								</h1>
								<ul className="flex flex-col md:items-start gap-2 w-fit font-medium text-xl">
									{membersMarketData.non_subscriber_subtitle_list.map((item, idx) => (
										// eslint-disable-next-line react/no-array-index-key
										<li className="list-item-checkmark-white" key={idx}>
											{item.subtitle_item}
										</li>
									))}
									{isBeforeOpenDate && (
										<li className="underline text-center md:text-left mt-3">
											<Link
												legacyBehavior
												href={membersMarketData.market_close_learn_more_path_link}
											>
												<a target="_blank">Learn more</a>
											</Link>
										</li>
									)}
								</ul>
							</div>
							{/* TODO: receive date to start countdown */}
							{!isBeforeOpenDate && (
								<div className="flex justify-center">
									<Countdown date={computedEndDate} renderer={renderer} />
								</div>
							)}
						</div>
					</div>
					<div className="bg-white flex w-full justify-center py-12 md:px-[60px] md:justify-start">
						<div className="flex flex-col w-fit place-content-center gap-4 md:pl-8">
							{/* Category title */}
							<span className="text-xl flex-wrap max-w-[300px] md:max-w-[400px] text-center md:text-left">
								{membersMarketData.non_subscriber_require_subscription}
							</span>
							<div className="flex flex-col md:flex-row justify-start gap-4 items-center w-full">
								<Link legacyBehavior href="/subscriptions">
									<a className="button button-primary">Purchase a Subscription</a>
								</Link>

								{!isLoggedIn && (
									<>
										<span className="items-center font-medium">OR</span>
										<Link legacyBehavior href="/login?continueUrl=members-market">
											<a className="button button-secondary">Login</a>
										</Link>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	)
}
